/**
 * Print pack → CSV. Read-only, client-side, built from the exact payload the
 * `print_pack` action already returned. No endpoint, no library: the schedule
 * on paper and the schedule in the spreadsheet come from the same rows and the
 * same resolved team names.
 */
import { formatTime, formatCalendarDate } from '@/lib/dates';
import { formatGameNumber } from '@/lib/gameNumber';

/** RFC 4180: quote anything with a comma, quote or newline; double the quotes. */
const cell = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (s === '') return '';
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Excel eagerly turns "9:00am" and "14:30" into dates. A leading apostrophe is
 * the text marker Excel honours on import, so times survive as written.
 */
const asText = (value: string | null | undefined): string =>
  value ? cell(`'${value}`) : '';

const timeText = (t: string | null | undefined) => asText(t ? formatTime(t) : '');
const dateText = (d: string | null | undefined) => asText(d ? formatCalendarDate(d) : '');

export const toCsv = (headers: string[], rows: unknown[][]): string =>
  [headers.map(cell).join(','), ...rows.map((r) => r.join(','))].join('\r\n') + '\r\n';

const slug = (s: string) =>
  (s || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'event';

/** UTF-8 BOM so accented team names open correctly in Excel on Windows. */
export const downloadCsv = (filename: string, csv: string) => {
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const csvFileName = (eventName: string, docKey: string, suffix?: string) =>
  `${slug(eventName)}-${docKey}${suffix ? `-${suffix}` : ''}.csv`;

/* ─── Schedule: one row builder, four sorts ─────────────────────────────── */

export const SCHEDULE_HEADERS = [
  'Game', 'Date', 'Start time', 'End time', 'Venue', 'Court', 'Division',
  'Pool / Bracket', 'Team A', 'Team B', 'Status', 'Score A', 'Score B',
];

/** Master schedule, by division, by court and by pool are this row, resorted. */
export const scheduleRow = (g: any): unknown[] => [
  cell(formatGameNumber(g)),
  dateText(g.scheduled_date),
  timeText(g.scheduled_start_time),
  timeText(g.scheduled_end_time),
  cell(g.venue_name),
  cell(g.court_name),
  cell(g.division),
  cell(g.bracket_label ?? (g.game_type === 'crossover' ? 'Crossover' : g.pool_name) ?? g.game_type),
  cell(g.team_a_name),
  cell(g.team_b_name),
  cell(g.game_status),
  cell(g.score_a),
  cell(g.score_b),
];

const cmp = (a: unknown, b: unknown) =>
  String(a ?? 'zzzz').localeCompare(String(b ?? 'zzzz'), undefined, { numeric: true });

const byTime = (a: any, b: any) =>
  cmp(a.scheduled_date, b.scheduled_date) ||
  cmp(a.scheduled_start_time, b.scheduled_start_time) ||
  cmp(a.court_name, b.court_name);

export const SCHEDULE_SORTS: Record<string, (a: any, b: any) => number> = {
  master: byTime,
  division: (a, b) => cmp(a.division, b.division) || byTime(a, b),
  court: (a, b) => cmp(a.court_name, b.court_name) || byTime(a, b),
  pool: (a, b) => cmp(a.pool_name, b.pool_name) || byTime(a, b),
};

export const scheduleCsv = (games: any[], sortKey: keyof typeof SCHEDULE_SORTS) =>
  toCsv(SCHEDULE_HEADERS, [...games].sort(SCHEDULE_SORTS[sortKey]).map(scheduleRow));

/* ─── Master grid: courts across, time slots down ───────────────────────── */

export const gridCsv = (dayGames: any[], courts: any[]) => {
  const used = courts.filter((c) => dayGames.some((g) => g.court_id === c.id));
  const times = [...new Set(dayGames.map((g) => g.scheduled_start_time).filter(Boolean))].sort();
  const headers = ['Time', ...used.map((c) => c.name)];
  const rows = times.map((t) => [
    timeText(t),
    ...used.map((c) => {
      const g = dayGames.find((x) => x.court_id === c.id && x.scheduled_start_time === t);
      return g ? cell(`${g.team_a_name} vs ${g.team_b_name}`) : '';
    }),
  ]);
  return toCsv(headers, rows);
};

/* ─── Rosters ───────────────────────────────────────────────────────────── */

export const ROSTER_HEADERS = ['Team', 'Division', 'Jersey', 'Player', 'Position', 'Captain'];

export const rostersCsv = (rosters: any[]) =>
  toCsv(
    ROSTER_HEADERS,
    rosters.flatMap((r: any) =>
      (r.players ?? []).map((p: any) => [
        cell(r.team_name),
        cell(r.division),
        asText(p.jersey_number != null ? String(p.jersey_number) : ''),
        cell(p.name),
        cell(p.position),
        p.is_captain ? 'Yes' : 'No',
      ]),
    ),
  );

/* ─── Standings — straight from the shared standings-engine payload ─────── */

export const STANDINGS_HEADERS = [
  'Division', 'Pool', 'Rank', 'Team', 'W', 'L', 'T', 'GP', 'PF', 'PA', 'Differential',
];

export const standingsCsv = (standings: any[]) =>
  toCsv(
    STANDINGS_HEADERS,
    standings.flatMap((s: any) =>
      (s.rows ?? []).map((r: any) => [
        cell(s.division),
        cell(r.pool_name),
        cell(r.pool_rank),
        cell(r.team_name),
        cell(r.wins),
        cell(r.losses),
        cell(r.ties),
        cell(r.games_played),
        cell(r.points_for),
        cell(r.points_against),
        cell(r.diff),
      ]),
    ),
  );

/** Score sheets are forms and brackets are shapes — neither is a table. */
export const CSV_DOCS = [
  'master', 'division', 'court', 'pool', 'grid', 'standings', 'rosters',
  'registrations', 'rostercounts', 'teamsummary', 'divisionsummary', 'gamesperday',
];

/* ─── Games per day (NCAA compliance) ───────────────────────────────────────
 * Rows arrive already worded by the page, so the export a compliance officer
 * opens says exactly what the printed page says.
 */
export const GAMES_PER_DAY_HEADERS = [
  'Team', 'Division', 'Date', 'Games that day', 'Limit', 'Games (number, time, court)',
];

export const gamesPerDayCsv = (
  rows: { team: string; division: string; date: string; games: number; limit: number; detail: string }[],
  headerNote?: string,
) => {
  const body = rows.length
    ? rows.map((r) => [
        cell(r.team), cell(r.division), cell(r.date), cell(r.games), cell(r.limit), cell(r.detail),
      ])
    : [[cell(headerNote ?? 'No team exceeds the limit.'), '', '', '', '', '']];
  return toCsv(GAMES_PER_DAY_HEADERS, body);
};

/* ─── Operator reports (registration, rosters counts, team + division summary) ─
 * All four are tables built from the same `print_pack` payload, so they print
 * and export through the machinery above. The registration list carries money
 * and contact details: operator pack only, never a public route.
 */

const yesNo = (v: unknown) => (v ? 'Yes' : 'No');
const money = (v: unknown) => (v == null ? '' : cell(Number(v).toFixed(2)));

export const REGISTRATIONS_HEADERS = [
  'Team', 'Division', 'Level', 'Coach first name', 'Coach last name', 'Coach email',
  'Coach phone', 'City', 'State', 'Registration status', 'Payment status',
  'Amount due', 'Amount paid', 'Amount outstanding', 'Registered',
];

export const registrationsCsv = (regs: any[]) =>
  toCsv(
    REGISTRATIONS_HEADERS,
    regs.map((r) => [
      cell(r.team_name),
      cell(r.division),
      cell(r.level),
      cell(r.coach_first_name),
      cell(r.coach_last_name),
      cell(r.coach_email),
      asText(r.coach_phone ?? ''),
      cell(r.city),
      cell(r.state),
      cell(r.registration_status),
      cell(r.payment_status),
      money(r.amount_due),
      money(r.amount_paid),
      money(r.amount_outstanding),
      dateText(r.registered_at ? String(r.registered_at).slice(0, 10) : ''),
    ]),
  );

export const ROSTER_COUNT_HEADERS = ['Team', 'Division', 'Players', 'Roster locked', 'Checked in'];

export const rosterCountsCsv = (regs: any[]) =>
  toCsv(
    ROSTER_COUNT_HEADERS,
    regs.map((r) => [
      cell(r.team_name),
      cell(r.division),
      cell(r.player_count ?? 0),
      yesNo(r.roster_locked),
      yesNo(r.checked_in_at),
    ]),
  );

export const TEAM_SUMMARY_HEADERS = [
  'Team', 'Division', 'Pool', 'Level', 'Coach', 'W', 'L', 'T', 'GP', 'PF', 'PA', 'Differential',
];

/**
 * One row per registered team. The record comes from the shared standings
 * payload — never recomputed here.
 */
export const teamSummaryRows = (regs: any[], standings: any[], pools: any[]) => {
  const record = new Map<string, any>();
  for (const s of standings ?? []) for (const r of s.rows ?? []) record.set(r.registration_id, r);
  const poolOf = new Map<string, string>();
  for (const p of pools ?? []) for (const t of p.teams ?? []) poolOf.set(t.registration_id, p.name);
  return regs.map((r) => {
    const rec = record.get(r.registration_id);
    return {
      team_name: r.team_name,
      division: r.division,
      pool_name: poolOf.get(r.registration_id) ?? null,
      level: r.level,
      coach_name: r.coach_name,
      wins: rec?.wins ?? 0,
      losses: rec?.losses ?? 0,
      ties: rec?.ties ?? 0,
      games_played: rec?.games_played ?? 0,
      points_for: rec?.points_for ?? 0,
      points_against: rec?.points_against ?? 0,
      diff: rec?.diff ?? 0,
    };
  });
};

export const teamSummaryCsv = (rows: ReturnType<typeof teamSummaryRows>) =>
  toCsv(
    TEAM_SUMMARY_HEADERS,
    rows.map((r) => [
      cell(r.team_name), cell(r.division), cell(r.pool_name), cell(r.level), cell(r.coach_name),
      cell(r.wins), cell(r.losses), cell(r.ties), cell(r.games_played),
      cell(r.points_for), cell(r.points_against), cell(r.diff),
    ]),
  );

export const DIVISION_SUMMARY_HEADERS = [
  'Level', 'Division', 'Pool', 'Teams', 'Pools', 'Games', 'Games completed',
];

const isComplete = (g: any) => g.game_status === 'completed';

/** Per division, then per pool inside it. Teams count registrations, not games. */
export const divisionSummaryRows = (divisions: string[], regs: any[], pools: any[], games: any[]) =>
  divisions.map((div) => {
    const divPools = (pools ?? []).filter((p: any) => p.division === div);
    const divGames = (games ?? []).filter((g: any) => g.division === div);
    return {
      division: div,
      teams: regs.filter((r) => r.division === div).length,
      pools: divPools.length,
      games: divGames.length,
      completed: divGames.filter(isComplete).length,
      rows: divPools.map((p: any) => {
        const pg = divGames.filter((g: any) => g.pool_id === p.id);
        return {
          pool_name: p.name,
          teams: (p.teams ?? []).length,
          games: pg.length,
          completed: pg.filter(isComplete).length,
        };
      }),
    };
  });

export const divisionSummaryCsv = (summary: ReturnType<typeof divisionSummaryRows>) =>
  toCsv(
    DIVISION_SUMMARY_HEADERS,
    summary.flatMap((d) => [
      ['Division', cell(d.division), '', cell(d.teams), cell(d.pools), cell(d.games), cell(d.completed)],
      ...d.rows.map((p) => [
        'Pool', cell(d.division), cell(p.pool_name), cell(p.teams), '', cell(p.games), cell(p.completed),
      ]),
    ]),
  );
